import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Heart, Star, Truck, Shield, Award, Repeat, ChevronLeft, ChevronRight, RotateCw, Camera } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useCart } from "../../context/CartContext";

export default function Product() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [tab, setTab] = useState("details");
  const [show360, setShow360] = useState(false);
  const { addToCart, toggleWishlist, isWishlisted } = useCart();

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => setProduct(r.data)).catch(() => navigate("/shop"));
  }, [id, navigate]);

  if (!product) return <div className="min-h-[60vh] flex items-center justify-center text-white/40 tracking-luxury uppercase text-sm">Loading…</div>;

  const handleAdd = () => {
    addToCart(product);
    toast.success(`${product.subtitle} added to cart`);
  };

  return (
    <div data-testid="product-page" className="bg-poche-black text-white">
      <div className="container-x py-10">
        <div className="text-[11px] tracking-luxury uppercase text-white/40">
          <Link to="/shop" className="hover:text-poche-gold">Shop</Link> / <span className="text-poche-gold">{product.subtitle}</span>
        </div>
      </div>

      <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-10 pb-24">
        {/* Image gallery */}
        <div className="lg:col-span-7">
          <div className="relative bg-white aspect-[4/3] overflow-hidden">
            <img src={product.images[activeImg]} alt={product.name} className="w-full h-full object-cover" />
            {product.is_legacy && (
              <span className="absolute top-5 left-5 bg-poche-gold text-poche-black px-3 py-1.5 text-[10px] tracking-luxury uppercase">
                Limited · 25 Pieces Worldwide
              </span>
            )}
            {/* Controls overlay */}
            <button onClick={() => setActiveImg((i) => Math.max(0, i - 1))} className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/85 w-9 h-9 flex items-center justify-center hover:bg-poche-gold">
              <ChevronLeft size={16} stroke="#0A0A0A" />
            </button>
            <button onClick={() => setActiveImg((i) => Math.min(product.images.length - 1, i + 1))} className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/85 w-9 h-9 flex items-center justify-center hover:bg-poche-gold">
              <ChevronRight size={16} stroke="#0A0A0A" />
            </button>
            <div className="absolute bottom-4 left-4 flex gap-2">
              <button data-testid="btn-360" onClick={() => setShow360(true)} className="bg-poche-black/80 text-white px-3 py-2 text-[10px] tracking-luxury uppercase flex items-center gap-2 hover:bg-poche-gold hover:text-poche-black">
                <RotateCw size={12} /> 360° View
              </button>
              <button data-testid="btn-tryon" onClick={() => toast("Virtual try-on opening — best viewed on mobile app")} className="bg-poche-black/80 text-white px-3 py-2 text-[10px] tracking-luxury uppercase flex items-center gap-2 hover:bg-poche-gold hover:text-poche-black">
                <Camera size={12} /> Virtual Try-On
              </button>
            </div>
          </div>
          <div className="grid grid-cols-4 gap-3 mt-3">
            {Array.from({ length: 4 }, (_, i) => product.images[i] || product.images[0]).map((img, i) => (
              <button key={i} onClick={() => setActiveImg(i < product.images.length ? i : 0)} className={`bg-white aspect-square overflow-hidden ${i === activeImg ? "ring-1 ring-poche-gold" : ""}`}>
                <img src={img} alt="" className="w-full h-full object-cover" />
                <span className="sr-only">{["Hero", "Front", "Three-quarter", "Packaging"][i]}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Details */}
        <div className="lg:col-span-5 lg:pl-8">
          <p className="text-[11px] tracking-luxury uppercase text-poche-gold">{product.collection}</p>
          <h1 className="font-serif text-5xl mt-3">{product.subtitle}</h1>
          <p className="text-white/55 text-sm tracking-luxury uppercase mt-1">{product.name}</p>
          <div className="flex items-center gap-3 mt-5">
            <div className="flex gap-1">
              {Array(5).fill(0).map((_, k) => <Star key={k} size={14} fill="#C9A864" stroke="none" />)}
            </div>
            <span className="text-xs text-white/55">{product.rating} · {product.reviews_count} reviews</span>
          </div>
          <div className="font-serif text-4xl text-poche-gold mt-7">${product.price?.toLocaleString()}</div>
          <p className="text-white/65 leading-relaxed mt-6">{product.description}</p>

          <div className="mt-8">
            <div className="text-[11px] tracking-luxury uppercase text-white/55 mb-3">Color</div>
            <div className="flex gap-3">
              {product.colors?.map((c, i) => (
                <button key={i} className="w-9 h-9 rounded-full border border-white/40 hover:border-poche-gold" style={{ background: c }} aria-label={`Color ${i + 1}`} />
              ))}
            </div>
          </div>

          <div className="flex gap-3 mt-10">
            {product.is_legacy ? (
              <Link to="/legacy" data-testid="legacy-cta-product" className="btn-gold flex-1 justify-center">Request Consultation</Link>
            ) : (
              <button data-testid="add-to-cart" onClick={handleAdd} className="btn-gold flex-1 justify-center">Add to Cart</button>
            )}
            <button
              data-testid="toggle-wish-product"
              onClick={() => { toggleWishlist(product); toast(isWishlisted(product.product_id) ? "Removed from wishlist" : "Saved to wishlist"); }}
              className="btn-outline-gold !px-5"
              aria-label="Wishlist"
            >
              <Heart size={14} fill={isWishlisted(product.product_id) ? "#C9A864" : "none"} />
            </button>
          </div>

          {/* Perks */}
          <div className="grid grid-cols-2 gap-4 mt-10 border-t border-white/10 pt-8">
            {[
              { i: Truck, t: "Free Global Shipping" },
              { i: Shield, t: "2-Year Warranty" },
              { i: Award, t: "Certificate Included" },
              { i: Repeat, t: "30-Day Returns" },
            ].map((p, i) => (
              <div key={i} className="flex items-center gap-3 text-xs text-white/65">
                <p.i size={16} strokeWidth={1.4} className="text-poche-gold" />
                {p.t}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs - Specs / Fit / Reviews */}
      <div className="border-t border-white/10">
        <div className="container-x py-12">
          <div className="flex gap-8 border-b border-white/10">
            {["details", "fit", "reviews"].map((t) => (
              <button
                key={t}
                data-testid={`tab-${t}`}
                onClick={() => setTab(t)}
                className={`text-[11px] tracking-luxury uppercase pb-4 -mb-px ${tab === t ? "text-poche-gold border-b border-poche-gold" : "text-white/55"}`}
              >
                {t}
              </button>
            ))}
          </div>
          <div className="py-10">
            {tab === "details" && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl">
                {[
                  ["Material", product.material],
                  ["Lens", product.lens_type],
                  ["Frame Width", product.frame_width + "mm"],
                  ["Lens Width", product.lens_width + "mm"],
                  ["Bridge Width", product.bridge_width + "mm"],
                  ["Temple Length", product.temple_length + "mm"],
                  ["Weight", product.weight + "g"],
                  ["Made In", product.is_legacy ? "Switzerland" : "Italy"],
                ].map(([l, v]) => (
                  <div key={l}>
                    <div className="text-[10px] tracking-luxury uppercase text-white/45">{l}</div>
                    <div className="font-serif text-2xl text-white mt-1">{v}</div>
                  </div>
                ))}
              </div>
            )}
            {tab === "fit" && (
              <div className="max-w-3xl text-white/70 leading-relaxed">
                <p>Our frames are engineered around a wider bridge geometry — between 16mm and 18mm — to sit naturally on a deeper nose contour without sliding.</p>
                <p className="mt-4">If you typically wear frames 142–150mm wide, this piece will fit. Visit our <Link to="/fit-guide" className="text-poche-gold underline underline-offset-4">Fit Guide</Link> to find your contour profile.</p>
              </div>
            )}
            {tab === "reviews" && (
              <div className="space-y-8 max-w-3xl">
                {(product.reviews || []).map((r, i) => (
                  <div key={i} className="border-t border-white/10 pt-6">
                    <div className="flex items-center gap-3">
                      <div className="flex gap-1">
                        {Array(r.rating).fill(0).map((_, k) => <Star key={k} size={12} fill="#C9A864" stroke="none" />)}
                      </div>
                      <span className="text-[11px] tracking-luxury uppercase text-white/55">{r.name}</span>
                    </div>
                    <div className="font-serif text-2xl mt-2">{r.title}</div>
                    <p className="text-white/65 mt-2">{r.body}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 360 Modal */}
      {show360 && (
        <div className="fixed inset-0 z-50 bg-poche-black/95 flex items-center justify-center p-6" onClick={() => setShow360(false)}>
          <div className="relative max-w-2xl w-full bg-white aspect-square overflow-hidden">
            <img src={product.images[activeImg]} alt="360" className="w-full h-full object-cover animate-spin" style={{ animationDuration: "8s" }} />
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-poche-black text-[11px] tracking-luxury uppercase">Simulated 360° view · Click to close</div>
          </div>
        </div>
      )}
    </div>
  );
}
